import { useEffect, useState } from "react";
import { Check, MessageCircle, Hotel, Car, Users, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, waLink } from "@/lib/api";

const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
const rupees = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const inputCls = "w-full bg-white border border-ink/15 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-terra/50";

export default function FestivalPackageDialog({ festival, onClose }) {
  const [form, setForm] = useState({ name: "", phone: "", guests: "2", hotel: "" });
  const [busy, setBusy] = useState(false);
  const [hotels, setHotels] = useState([]);
  useEffect(() => {
    api.get("/hotels").then((r) => {
      const list = [...r.data].sort((a, b) => (a.tier === "budget" ? 0 : 1) - (b.tier === "budget" ? 0 : 1));
      setHotels(list);
      const budget = list.find((h) => h.tier === "budget" && h.partner);
      if (budget) setForm((fm) => (fm.hotel ? fm : { ...fm, hotel: budget.id }));
    }).catch(() => {});
  }, []);
  if (!festival) return null;
  const f = festival;
  const minPrice = (h) => Math.min(...h.rooms.map((r) => Number(r.price) || Infinity));
  const premiumMin = hotels.filter((h) => h.tier === "premium").reduce((m, h) => Math.min(m, minPrice(h)), Infinity);
  const chosen = hotels.find((h) => h.id === form.hotel);
  const savings = chosen && chosen.tier === "budget" && Number.isFinite(premiumMin) ? (premiumMin - minPrice(chosen)) * (f?.package_nights || 1) : 0;
  const dates = `${fmt(f.start_date)}${f.end_date && f.end_date !== f.start_date ? ` – ${fmt(f.end_date)}` : ""}`;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) return toast.error("Add your name and WhatsApp number");
    setBusy(true);
    const hotel = hotels.find((h) => h.id === form.hotel);
    const summary = `Festival package: ${f.name} (${dates}) · ${f.package_nights} nights · ${form.guests} guests · from ${rupees(f.package_price)} for two${hotel ? ` · Stay: ${hotel.name}` : ""}. Includes: ${f.package_includes.join(", ")}.`;
    try {
      await api.post("/enquiries", { name: form.name, phone: form.phone, service: `Festival Package: ${f.name}`, travel_dates: dates, message: summary, kind: "festival", estimate: Number(f.package_price) || 0 });
      toast.success("Request saved! Opening WhatsApp to confirm.");
      window.open(waLink(`Hi Random Rider! I'd like to book the ${f.name} festival package.\n${summary}\nName: ${form.name}`), "_blank");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save. Please try WhatsApp directly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!festival} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="max-w-lg bg-sand border-ink/10 max-h-[90vh] overflow-y-auto" data-testid="festival-package-dialog">
        <DialogHeader>
          <p className="font-mono text-[11px] uppercase tracking-widest text-terra flex items-center gap-1.5"><Sparkles size={12} /> Festival package · {dates}</p>
          <DialogTitle className="font-serif text-2xl text-ink text-left" data-testid="festival-package-title">{f.name}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-ink/70 leading-relaxed">{f.description}</p>
        <div className="bg-ink text-sand rounded-2xl p-5">
          <div className="flex items-end justify-between">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-gold">From, for two</p>
              <p className="font-serif text-4xl font-black" data-testid="festival-package-price">{rupees(f.package_price)}</p>
            </div>
            <div className="text-right text-xs text-sand/70 space-y-1">
              <p className="flex items-center justify-end gap-1.5"><Hotel size={12} /> {f.package_nights} nights</p>
              <p className="flex items-center justify-end gap-1.5"><Car size={12} /> AC cab</p>
              <p className="flex items-center justify-end gap-1.5"><Users size={12} /> {f.place}</p>
            </div>
          </div>
          <ul className="mt-4 grid sm:grid-cols-2 gap-1.5" data-testid="festival-package-includes">
            {f.package_includes.map((x) => <li key={x} className="flex items-start gap-1.5 text-xs text-sand/85"><Check size={13} className="text-terra shrink-0 mt-0.5" /> {x}</li>)}
          </ul>
          {f.approximate && <p className="mt-3 text-[10px] text-sand/40">Festival dates follow the lunar calendar — we confirm exact dates with you before booking.</p>}
        </div>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className={inputCls} data-testid="festival-package-name" />
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="WhatsApp number" className={inputCls} data-testid="festival-package-phone" />
          {hotels.length > 0 && (
            <select value={form.hotel} onChange={(e) => setForm({ ...form, hotel: e.target.value })} className={`${inputCls} col-span-2`} data-testid="festival-package-hotel">
              <option value="">Preferred stay — let us suggest</option>
              {hotels.map((h) => <option key={h.id} value={h.id}>{h.tier === "budget" && h.partner ? "★ " : ""}{h.name} · from ₹{minPrice(h).toLocaleString("en-IN")}/night{h.tier === "budget" ? " · budget" : ""}</option>)}
            </select>
          )}
          {savings > 0 && (
            <p className="col-span-2 -mt-1 text-xs text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2 flex items-center gap-1.5" data-testid="festival-package-savings">
              <Sparkles size={12} /> Save ₹{savings.toLocaleString("en-IN")} over {f.package_nights} nights vs a premium hotel — {chosen.name}, beach side behind the Lighthouse.
            </p>
          )}
          {chosen && Number(chosen.advance) > 0 && (
            <p className="col-span-2 -mt-1 text-xs text-ink bg-gold/20 border border-gold/40 rounded-xl px-3 py-2" data-testid="festival-package-advance">₹{Number(chosen.advance).toLocaleString("en-IN")} advance confirms your room at {chosen.name} · balance at check-in</p>
          )}
          <select value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} className={`${inputCls} col-span-2`} data-testid="festival-package-guests">
            {[1, 2, 3, 4, 5, 6, 8, 10].map((n) => <option key={n} value={n}>{n} guest{n > 1 ? "s" : ""}</option>)}
          </select>
          <button type="submit" disabled={busy} data-testid="festival-package-submit"
            className="col-span-2 flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold py-3.5 rounded-full hover:brightness-110 transition-all disabled:opacity-60">
            <MessageCircle size={16} /> {busy ? "Saving…" : "Request this package on WhatsApp"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
